// Mirror of pkg/emailcheck — keep in sync.

const POPULAR_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'btinternet.com',
  'sky.com',
  'virginmedia.com',
  'ntlworld.com',
  'talktalk.net',
  'tiscali.co.uk',
  'blueyonder.co.uk',
  'aol.com',
  'aol.co.uk',
];

const SUGGEST_THRESHOLD = 2;

// Fast client check only — the server is authoritative.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(raw: string): boolean {
  return EMAIL_REGEX.test(raw.trim());
}

export function suggestEmail(raw: string): string | null {
  const at = raw.lastIndexOf('@');
  if (at <= 0 || at === raw.length - 1) return null;
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1).trim().toLowerCase();
  if (!domain) return null;
  if (POPULAR_DOMAINS.includes(domain)) return null;

  let best = SUGGEST_THRESHOLD + 1;
  let bestDomain: string | null = null;
  for (const candidate of POPULAR_DOMAINS) {
    if (Math.abs(candidate.length - domain.length) > SUGGEST_THRESHOLD) continue;
    const dist = damerauLevenshtein(domain, candidate);
    if (dist < best) {
      best = dist;
      bestDomain = candidate;
    }
  }
  if (best > SUGGEST_THRESHOLD || !bestDomain) return null;
  return `${local}@${bestDomain}`;
}

// Adjacent transposition is a single edit (gmial → gmail = 1).
function damerauLevenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (a.length < b.length) [a, b] = [b, a];

  const la = a.length;
  const lb = b.length;
  let prev2 = new Array<number>(lb + 1).fill(0);
  let prev = new Array<number>(lb + 1);
  let curr = new Array<number>(lb + 1).fill(0);
  for (let j = 0; j <= lb; j++) prev[j] = j;

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        const v = prev2[j - 2] + 1;
        if (v < curr[j]) curr[j] = v;
      }
    }
    [prev2, prev, curr] = [prev, curr, prev2];
  }
  return prev[lb];
}

// Anti-XSS URL allowlist for any href not hard-coded as a literal
// (config/env/user/query-derived). See the portal Validation & XSS rules.
const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

export function isSafeHref(url: string): boolean {
  try {
    return SAFE_SCHEMES.has(new URL(url).protocol);
  } catch {
    return false;
  }
}
